import * as React from 'react'
import { StarIcon } from '@heroicons/react/16/solid'
import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'
import { Pencil, Trash2, Check, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/AlertDialog'
import useAppStore from '@/store/useAppStore'
import useGetSavedViews from '@/services/api/cash-flow/useGetSavedViews'
import useUpdateSavedView from '@/services/api/cash-flow/useUpdateSavedView'
import useDeleteSavedView from '@/services/api/cash-flow/useDeleteSavedView'
import type { SavedViewRecord } from '@/pages/dashboard/savedViewsMath'

export default function SavedViewsMenu() {
  const [open, setOpen] = React.useState(false)
  const [renamingId, setRenamingId] = React.useState<number | null>(null)
  const [renameValue, setRenameValue] = React.useState('')

  const { data, status } = useGetSavedViews({ surface: 'dashboard' })
  const views: SavedViewRecord[] = data?.data ?? []

  const setSelectedVaultIds = useAppStore((s) => s.setDashboardSelectedVaultIds)
  const setSplitByVault = useAppStore((s) => s.setDashboardSplitByVault)
  const setNumberOfPeriods = useAppStore((s) => s.setNumberOfPeriods)
  const setPeriodType = useAppStore((s) => s.setPeriodType)

  const { action: updateView } = useUpdateSavedView()
  const { action: deleteView } = useDeleteSavedView()

  // Applying a view is a purely local state change — it never calls a team
  // settings endpoint.
  function applyView(view: SavedViewRecord) {
    const config = view.config ?? {}
    setSelectedVaultIds(config.vault_ids ?? null)
    setSplitByVault(config.split_by_vault ?? false)
    if (config.horizon) setNumberOfPeriods(config.horizon)
    if (config.period) setPeriodType(config.period)
    setOpen(false)
  }

  function startRename(view: SavedViewRecord) {
    setRenamingId(view.id)
    setRenameValue(view.name)
  }

  async function saveRename(view: SavedViewRecord) {
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== view.name) {
      await updateView({ id: view.id, name: trimmed })
    }
    setRenamingId(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-accent/30 bg-accent-tr-08 px-2.5 py-1.5 text-sm font-semibold text-txt-accent"
        >
          <StarIcon className="size-3.5 text-[hsl(44,66%,50%)]" />
          Saved views
          <ChevronDownIcon className="size-3.5" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-2" align="start">
        {status === 'pending' && (
          <div className="px-1.5 py-2 text-xs text-txt-secondary">Loading…</div>
        )}

        {status === 'success' && views.length === 0 && (
          <div className="px-1.5 py-2 text-xs text-txt-secondary">
            No saved views yet — set up the dashboard the way you want and
            use &quot;Save view&quot; to keep it.
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          {views.map((view) => (
            <div
              key={view.id}
              className="group flex items-center gap-1 rounded-md px-1.5 py-1.5 hover:bg-gray-tr-08"
            >
              {renamingId === view.id ? (
                <>
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename(view)
                      if (e.key === 'Escape') setRenamingId(null)
                    }}
                    className="min-w-0 flex-1 rounded border border-primary px-1.5 py-0.5 text-sm"
                  />
                  <button
                    type="button"
                    aria-label="Save name"
                    onClick={() => saveRename(view)}
                  >
                    <Check className="size-3.5 text-[hsl(137,57%,41%)]" />
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel rename"
                    onClick={() => setRenamingId(null)}
                  >
                    <X className="size-3.5 text-txt-secondary" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => applyView(view)}
                    className="min-w-0 flex-1 truncate text-left text-sm text-txt-primary"
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    aria-label={view.is_default ? 'Default view' : 'Set as default'}
                    onClick={() => updateView({ id: view.id, is_default: true })}
                  >
                    <StarIcon
                      className={`size-3.5 ${
                        view.is_default
                          ? 'text-[hsl(44,66%,50%)]'
                          : 'text-txt-secondary opacity-0 group-hover:opacity-100'
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    aria-label="Rename view"
                    onClick={() => startRename(view)}
                    className="opacity-0 group-hover:opacity-100"
                  >
                    <Pencil className="size-3.5 text-txt-secondary" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        aria-label="Delete view"
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="size-3.5 text-[hsl(356,72%,47%)]" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete &quot;{view.name}&quot;?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {view.is_default
                            ? "This is your default view. Deleting it leaves this surface with no default — the team's settings apply until you set a new one."
                            : "This can't be undone."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteView(view.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
